import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { CheckCircle, XCircle, ArrowLeft, DollarSign, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

const ModeratorPanel = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [pendingReports, setPendingReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [estimatedCost, setEstimatedCost] = useState('');
  const [verificationComments, setVerificationComments] = useState('');
  const [expenses, setExpenses] = useState([{ category: 'Materials', amount: 0 }]);

  useEffect(() => {
    if (!user || user.role !== 'moderator') {
      navigate('/');
      return;
    }
    fetchPendingReports();
  }, [user]);

  const fetchPendingReports = async () => {
    try {
      const res = await axios.get(`${API}/moderator/reports/pending`);
      setPendingReports(res.data);
    } catch (err) {
      toast.error('Failed to fetch pending reports');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      const cost = parseInt(estimatedCost);
      if (!cost || cost < 1000) {
        toast.error('Please enter a valid cost (minimum ₹1000)');
        return;
      }

      await axios.post(`${API}/moderator/reports/${selectedReport.id}/approve`, {
        action: 'approve',
        estimated_cost: cost,
        verification_comments: verificationComments,
        expenses: expenses.filter(e => e.amount > 0)
      });

      toast.success('Report approved successfully');
      setShowApproveModal(false);
      fetchPendingReports();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve report');
    }
  };

  const handleReject = async () => {
    if (!verificationComments.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      await axios.post(`${API}/moderator/reports/${selectedReport.id}/approve`, {
        action: 'reject',
        verification_comments: verificationComments
      });

      toast.success('Report rejected');
      setShowRejectModal(false);
      fetchPendingReports();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject report');
    }
  };

  const resetForm = () => {
    setEstimatedCost('');
    setVerificationComments('');
    setExpenses([{ category: 'Materials', amount: 0 }]);
    setSelectedReport(null);
  };

  const addExpense = () => {
    setExpenses([...expenses, { category: '', amount: 0 }]);
  };

  const updateExpense = (index, field, value) => {
    const newExpenses = [...expenses];
    newExpenses[index][field] = field === 'amount' ? parseInt(value) || 0 : value;
    setExpenses(newExpenses);
  };

  const removeExpense = (index) => {
    setExpenses(expenses.filter((_, i) => i !== index));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-white hover:text-blue-100 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Home
          </button>
          <h1 className="text-4xl font-bold mb-2">Moderator Panel</h1>
          <p className="text-blue-100">Review and approve pending reports</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Pending Reports ({pendingReports.length})</h2>
        </div>

        {pendingReports.length === 0 ? (
          <div className="card p-12 text-center">
            <FileText size={48} className="mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500">No pending reports to review</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingReports.map((report) => (
              <div key={report.id} className="card p-6">
                <div className="flex gap-6">
                  {/* Photo */}
                  {report.photos.length > 0 && (
                    <img
                      src={report.photos[0]}
                      alt={report.title}
                      className="w-32 h-32 object-cover rounded-lg flex-shrink-0"
                    />
                  )}

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{report.title}</h3>
                    <p className="text-gray-600 mb-3">{report.description}</p>
                    <div className="flex flex-wrap gap-3 text-sm">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full capitalize">
                        {report.category}
                      </span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full capitalize">
                        {report.urgency} priority
                      </span>
                      <span className="text-gray-500">{formatDate(report.created_at)}</span>
                      <span className="text-gray-500">{report.location.address}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <Button
                      onClick={() => {
                        setSelectedReport(report);
                        setShowApproveModal(true);
                      }}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle size={18} className="mr-2" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => {
                        setSelectedReport(report);
                        setShowRejectModal(true);
                      }}
                      variant="outline"
                      className="border-red-600 text-red-600 hover:bg-red-50"
                    >
                      <XCircle size={18} className="mr-2" />
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Approve Modal */}
      <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Approve Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Estimated Cost (₹)</label>
              <Input
                type="number"
                placeholder="Enter estimated cost"
                value={estimatedCost}
                onChange={(e) => setEstimatedCost(e.target.value)}
                min="1000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Expenses Breakdown</label>
              {expenses.map((expense, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <Input
                    placeholder="Category (e.g., Materials)"
                    value={expense.category}
                    onChange={(e) => updateExpense(idx, 'category', e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    placeholder="Amount"
                    value={expense.amount || ''}
                    onChange={(e) => updateExpense(idx, 'amount', e.target.value)}
                    className="w-32"
                  />
                  {expenses.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => removeExpense(idx)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addExpense} className="mt-2">
                Add Expense
              </Button>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Verification Comments</label>
              <Textarea
                placeholder="Add verification notes..."
                value={verificationComments}
                onChange={(e) => setVerificationComments(e.target.value)}
                rows={3}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button onClick={handleApprove} className="flex-1">
                Approve Report
              </Button>
              <Button variant="outline" onClick={() => setShowApproveModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Rejection Reason</label>
              <Textarea
                placeholder="Explain why this report is being rejected..."
                value={verificationComments}
                onChange={(e) => setVerificationComments(e.target.value)}
                rows={4}
                required
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleReject}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                Reject Report
              </Button>
              <Button variant="outline" onClick={() => setShowRejectModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModeratorPanel;
